import { Box, Dropdown, Text, Tooltip } from "@100mslive/react-ui";
import {
  CheckIcon,
  GlobeIcon,
  HeadphonesIcon
} from "@100mslive/react-icons";
import { ROLES, ROLES_CONVERT } from "../../common/roles";
import React, {
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  selectPeers,
  selectRolesMap,
  useHMSActions,
  useHMSStore,
} from "@100mslive/react-sdk";

import { APP_DATA } from "../../common/constants";
import IconButton from "../../IconButton";
import { LiveHodoContext } from "../../contexts/LiveHodoContext";
import { ToastManager } from "../Toast/ToastManager";
import { convertRolesName } from "../../common/utils";
import { useDropdownList } from "../hooks/useDropdownList";
import { useDropdownSelection } from "../hooks/useDropdownSelection";
import { useFilteredRoles } from "../../common/hooks";
import { useNavigation } from "../hooks/useNavigation";
import { useParams } from "react-router-dom";
import { useSetAppDataByKey } from "../AppData/useUISettings";
import { useTranslation } from "react-i18next";

const MODALS = {
  SELECT_LANG: "selectLang",
};

export const CabinSelectLangs = () => {
  const {
    stateCtx: { selectedCabinData, selectedRole, cabins },
    setSelectedCabinData,
    setSelectedRole,
  } = useContext(LiveHodoContext);
  const { t } = useTranslation();
  const selectionBg = useDropdownSelection();

  const hmsActions = useHMSActions();
  const navigate = useNavigation();

  const [openModals, setOpenModals] = useState(new Set());
  const [hasPublishingPeers, setHasPublishingPeers] = useState(false);
  const [peersPublishRole, setPeersPublishRole] = useState([]);
  const [selectedCabin, setSelectedCabin] = useState(null);
  // const [isPeerMuted, setIsPeerMuted] = useState(false);

  const [pinnedTrackId, setPinnedTrackId] = useSetAppDataByKey(
    APP_DATA.pinnedTrackId
  );
  const allRoleNames = useFilteredRoles();
  const roles = useHMSStore(selectRolesMap);
  const peers = useHMSStore(selectPeers);
  const roleNames = useMemo(() => {
    return allRoleNames.filter(
      role =>
        role === ROLES.PRESENTER ||
        role === ROLES.PANELIST ||
        role === ROLES.E_V ||
        role === ROLES.F_V
    );
  }, [allRoleNames]);

  const { roomId: urlRoomId, role: userRole } = useParams(); // from the url

  useDropdownList({ open: openModals.size > 0, name: "selectLang" });

  const updateState = (modalName, value) => {
    setOpenModals(modals => {
      const copy = new Set(modals);
      if (value) {
        copy.add(modalName);
      } else {
        copy.delete(modalName);
      }
      return copy;
    });
  };

  useEffect(() => {
    const hasPublishingPeersRaw = peers.some(peer => {
      // peer able to publish
      if (peer?.roleName && roles[peer?.roleName]) {
        return !!roles[peer?.roleName]?.publishParams?.allowed.length;
      }
      return true;
    });
    setHasPublishingPeers(hasPublishingPeersRaw);
  }, [peers]);

  useEffect(() => {
    const listPeersNotGuestRoleRaw = peers.filter(
      peer =>
        peer.roleName === ROLES.PRESENTER ||
        peer.roleName === ROLES.PANELIST ||
        peer.roleName === ROLES.E_V ||
        peer.roleName === ROLES.F_V
    );

    // const listPeersNotGuestRole = listPeersNotGuestRoleRaw.filter(
    //   (peer, index, self) =>
    //     self.findIndex(p => p.roleName === peer.roleName) === index
    // );

    const roles = listPeersNotGuestRoleRaw.map(peer => {
      const hasRole = roleNames.includes(peer.roleName);
      if (hasRole) {
        return peer;
      }
    });

    setPeersPublishRole(roles);
  }, [peers]);

  useEffect(() => {
    if (selectedRole) {
      if (peersPublishRole.length) {
        const hasTranslator = peersPublishRole.find(
          peer => peer.roleName === selectedRole
        );
        if (!hasTranslator) {
          handleSelectedCabin(
            ROLES.PRESENTER,
            true,
            t("fm_interpretation_end", {
              text1: t("obj_roles." + ROLES_CONVERT[convertRolesName(selectedRole)]),
              text2: t("obj_roles." + ROLES_CONVERT[convertRolesName(ROLES.PRESENTER)])
            })
          );
          return;
        }
      }
      handleSelectedCabin(selectedRole, false);
      return;
    }

    if (peersPublishRole.length) {
      const hasPresenter = peersPublishRole.find(
        peer => peer.roleName === ROLES.PRESENTER
      );

      if (hasPresenter) {
        handleSelectedCabin(ROLES.PRESENTER, false);
        setPinnedTrackId(hasPresenter?.audioTrack || hasPresenter?.videoTrack);
      }
    }
  }, [peersPublishRole, selectedRole]);

  const handleToggleVolume = useCallback(
    (volume = 0, peers) => {
      if (peers?.length > 0) {
        peers?.map(peer => {
          if (peer?.auxiliaryTracks?.length > 0) {
            peer?.auxiliaryTracks?.forEach(track => {
              hmsActions.setVolume(volume, track);
            });
          }
          if (peer?.audioTrack) {
            hmsActions.setVolume(volume, peer?.audioTrack);
          }
        });
        return;
      }

      peersPublishRole?.map(peer => {
        if (peer?.auxiliaryTracks?.length > 0) {
          peer?.auxiliaryTracks?.forEach(track => {
            hmsActions.setVolume(volume, track);
          });
        }
        if (peer?.audioTrack) {
          hmsActions.setVolume(volume, peer?.audioTrack);
        }
      });
    },
    [peersPublishRole]
  );

  const handleClearCabin = useCallback(() => {
    setSelectedRole("");
    setSelectedCabin(null);
    handleToggleVolume(0);
    setSelectedCabinData(null);
    ToastManager.addToast({
      title: t("fm_you_have_left_cabin"),
    });
  }, [handleToggleVolume]);

  const handleSelectedCabin = useCallback(
    (role, isPushNotify = true, msg) => {
      handleToggleVolume(0);

      const timeout = setTimeout(() => {
        const peersByRole = peersPublishRole.filter(
          peer => peer.roleName === role
        );

        handleToggleVolume(100, peersByRole);
      }, 1000);

      setSelectedRole(role);

      if (isPushNotify) {
        ToastManager.addToast({
          title:
            msg || t("fm_interpretation_select", { text: t("obj_roles." + ROLES_CONVERT[convertRolesName(role)]) })
        });
      }

      return () => clearTimeout(timeout);
    },
    [peersPublishRole]
  );

  const hasRoleInPeers = useCallback(
    role => {
      return !!peersPublishRole.find(peer => peer.roleName === role);
    },
    [peersPublishRole]
  );

  return (
    <Fragment>
      {hasPublishingPeers ? (
        <Dropdown.Root
          open={openModals.has(MODALS.SELECT_LANG)}
          onOpenChange={value => updateState(MODALS.SELECT_LANG, value)}
        >
          <Dropdown.Trigger asChild data-testid="cabin_select_lang_btn">
            <IconButton>
              <Tooltip title={t("fm_toggle_interpretation")}>
                <Box>
                  <GlobeIcon />
                </Box>
              </Tooltip>
            </IconButton>
          </Dropdown.Trigger>

          <Dropdown.Content
            sideOffset={5}
            align="center"
            css={{
              maxHeight: "$96",
              "@md": { w: "$64" },
              "div[role='separator']:first-child": {
                display: "none",
              },
            }}
          >
            {roleNames.map((role, index) => (
              <Dropdown.Item
                key={index}
                css={{
                  justifyContent: "space-between",
                  opacity: hasRoleInPeers(role) ? 1 : 0.35,
                  cursor: hasRoleInPeers(role) ? "pointer" : "not-allowed",
                  pointerEvents: hasRoleInPeers(role) ? "auto" : "none",
                  bg: selectedRole === role ? selectionBg : undefined,
                }}
                onClick={() => handleSelectedCabin(role)}
              >
                <Text variant="sm" css={{ mr: "auto" }}>
                  { t("obj_roles." + ROLES_CONVERT[convertRolesName(role)]) }
                </Text>

                {selectedRole === role ? <CheckIcon /> : <HeadphonesIcon />}
              </Dropdown.Item>
            ))}
          </Dropdown.Content>
        </Dropdown.Root>
      ) : (
        <IconButton>
          <Tooltip title={t("fm_toggle_interpretation")}>
            <Box>
              <GlobeIcon />
            </Box>
          </Tooltip>
        </IconButton>
      )}
    </Fragment>
  );
};
